/**
 * Zustand navigation store for HFT Studio.
 *
 * Manages a view-stack for breadcrumb navigation:
 *   Calendar → Day → Segment → (chain or symbol detail)
 */
import { create } from 'zustand';
import type { ViewType, NavState } from './types';

interface ViewFrame {
  view: ViewType;
  nav: NavState;
}

interface HftStudioStore {
  stack: ViewFrame[];
  current: ViewFrame;

  /** Push a new view onto the stack. */
  push: (view: ViewType, nav: Partial<NavState>) => void;

  /** Pop back one level (min: calendar). */
  pop: () => void;

  /** Pop to a specific depth (0 = calendar). */
  popTo: (depth: number) => void;

  /** Reset to calendar root. */
  reset: () => void;

  /** Date range for the calendar view. */
  calendarFrom: string;
  calendarTo: string;
  setCalendarRange: (from: string, to: string) => void;
}

const INITIAL_NAV: NavState = { date: null, segment: null, symbol: null, underlying: null };
const INITIAL_FRAME: ViewFrame = { view: 'calendar', nav: { ...INITIAL_NAV } };

export const useHftStore = create<HftStudioStore>((set, get) => {
  // Default: last 6 months.
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const defaultFrom = sixMonthsAgo.toISOString().slice(0, 10);
  const defaultTo = now.toISOString().slice(0, 10);

  return {
    stack: [INITIAL_FRAME],
    current: INITIAL_FRAME,
    calendarFrom: defaultFrom,
    calendarTo: defaultTo,

    push: (view, navPatch) => {
      const { stack, current } = get();
      const merged: NavState = { ...current.nav, ...navPatch };
      const frame: ViewFrame = { view, nav: merged };
      set({ stack: [...stack, frame], current: frame });
    },

    pop: () => {
      const { stack } = get();
      if (stack.length <= 1) return;
      const next = stack.slice(0, -1);
      set({ stack: next, current: next[next.length - 1] });
    },

    popTo: (depth) => {
      const { stack } = get();
      const clamped = Math.max(1, Math.min(depth + 1, stack.length));
      const next = stack.slice(0, clamped);
      set({ stack: next, current: next[next.length - 1] });
    },

    reset: () => set({ stack: [INITIAL_FRAME], current: INITIAL_FRAME }),

    setCalendarRange: (from, to) => set({ calendarFrom: from, calendarTo: to }),
  };
});
